import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import axios from '../utils/axiosConfig';
import { useWorkflowPrompt } from '../contexts/WorkflowPromptContext';
import { fetchNotifications } from '../redux/actions/notificationActions';
import { listAppointments } from '../redux/actions/appointmentActions';
import { getOutlookAccessTokenForWorkflow } from '../utils/outlookToken';

const TIMEOUT_SECONDS = 30;

const WorkflowPromptDialog = () => {
  const { t } = useTranslation(['workflows', 'buttons', 'common']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.userLoaded || {});
  const { currentPrompt, removePendingPrompt, showNextPrompt, completeCurrentAndEnqueueNext } = useWorkflowPrompt();
  const [timeoutSeconds, setTimeoutSeconds] = useState(TIMEOUT_SECONDS);
  const [loading, setLoading] = useState(false);

  const handleCancel = useCallback(async () => {
    if (!currentPrompt || loading) return;

    try {
      setLoading(true);
      const response = await axios.post(`/api/workflows/executions/${currentPrompt.executionId}/cancel`);
      const nextPending = response?.data?.nextPendingPrompt ?? null;
      if (nextPending) {
        completeCurrentAndEnqueueNext(currentPrompt.executionId, nextPending);
      } else {
        removePendingPrompt(currentPrompt.executionId);
        showNextPrompt();
      }
    } catch (error) {
      console.error('Error cancelling workflow execution:', error);
      removePendingPrompt(currentPrompt.executionId);
      showNextPrompt();
    } finally {
      setLoading(false);
    }
  }, [currentPrompt, loading, removePendingPrompt, showNextPrompt, completeCurrentAndEnqueueNext]);

  const handleConfirm = useCallback(async () => {
    if (!currentPrompt || loading) return;

    setLoading(true);
    let accessToken = null;
    if (currentPrompt.actionType === 'send_email') {
      try {
        accessToken = await getOutlookAccessTokenForWorkflow();
      } catch (err) {
        console.error('Outlook sign-in failed or was cancelled:', err);
        setLoading(false);
        return;
      }
    }

    try {
      const body = accessToken ? { accessToken } : {};
      const response = await axios.post(`/api/workflows/executions/${currentPrompt.executionId}/confirm`, body);
      const hadTimesheetRedirect = currentPrompt.openTimesheetOnConfirm && currentPrompt.caseId;
      const caseIdForRedirect = currentPrompt.caseId;
      const nextPending = response?.data?.nextPendingPrompt ?? null;
      completeCurrentAndEnqueueNext(currentPrompt.executionId, nextPending);
      if (user?._id) {
        dispatch(fetchNotifications(user._id));
      }
      dispatch(listAppointments());
      if (hadTimesheetRedirect && caseIdForRedirect) {
        navigate('/timesheets/create', { state: { caseId: caseIdForRedirect } });
      }
    } catch (error) {
      console.error('Error confirming workflow execution:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPrompt, loading, completeCurrentAndEnqueueNext, user, dispatch, navigate]);

  useEffect(() => {
    if (currentPrompt) {
      setTimeoutSeconds(TIMEOUT_SECONDS);
      const interval = setInterval(() => {
        setTimeoutSeconds(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            handleCancel();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentPrompt, handleCancel]);

  const handleClose = () => {
    // Default to cancel when user closes dialog
    handleCancel();
  };

  if (!currentPrompt) return null;

  return (
    <Dialog
      open={!!currentPrompt}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{ sx: { borderRadius: 0 } }}
    >
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 600, fontFamily: 'Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif' }}>
        {currentPrompt.workflowName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ fontSize: '14px', mb: 2 }}>
            {currentPrompt.promptMessage}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '12px', color: '#666' }}>
            {t('workflows:timeout_notice', { seconds: timeoutSeconds }) || `This dialog will automatically cancel in ${timeoutSeconds} seconds.`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ borderRadius: 0, textTransform: 'none' }}
          disabled={loading}
        >
          {t('buttons:no') || 'No'}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{ borderRadius: 0, textTransform: 'none' }}
          disabled={loading}
        >
          {t('buttons:yes') || 'Yes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowPromptDialog;
